import { useEffect, useState } from 'react';

import Spinner from '../../../../reusable/spinner/spinner.component';
import Client from '../../../../../tools/client';

import {
  Container,
  FieldGroup,
  FieldSection,
  Label,
  Input,
  Toggle,
  ButtonRow,
  Button,
  Description
} from '../../configuration-sections.styles';

const client = new Client();

const ReshipConfigurationDisplay = () => {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    const fetchConfig = async () => {
      const res = await client.setup();
      const shop = res.configuration?.shop?.reship || {};
      setForm({
        active: shop.active === 'true' || shop.active === true,
        windowDays: shop.windowDays || '',
        maxRequestsPerOrder: shop.maxRequestsPerOrder || '',
        maxRequestsPerItem: shop.maxRequestsPerItem || '',
        maxActiveRequestsPerCustomer: shop.maxActiveRequestsPerCustomer || '',
        requireReason: shop.requireReason === 'true' || shop.requireReason === true,
        requirePictures: shop.requirePictures === 'true' || shop.requirePictures === true,
        shippingFeeCents: shop.shippingFeeCents || '',
        allowRetryAfterRejection: shop.allowRetryAfterRejection === 'true' || shop.allowRetryAfterRejection === true,
        allowMultipleItemsPerReship: shop.allowMultipleItemsPerReship === 'true' || shop.allowMultipleItemsPerReship === true,
        adminApprovalRequired: shop.adminApprovalRequired === 'true' || shop.adminApprovalRequired === true,
        notifyAdminOnRequest: shop.notifyAdminOnRequest === 'true' || shop.notifyAdminOnRequest === true,
        notifyCustomerOnDecision: shop.notifyCustomerOnDecision === 'true' || shop.notifyCustomerOnDecision === true,
      });
      setLoading(false);
    };
    fetchConfig();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleToggle = (key) => {
    setForm({ ...form, [key]: !form[key] });
  };

  const handleSave = async () => {
    for (const [key, value] of Object.entries(form)) {
      await client.updateConfigurationByKey(`shop.reship.${key}`, {
        value: typeof value === 'boolean' ? String(value) : value
      });
    }
    setEditing(false);
  };

  if (loading) return <Spinner />;

  return (
    <Container>
      <FieldSection>
        <FieldGroup>
          <Label>Enable Reship Feature</Label>
          <Description>Toggle the entire reship system on or off for the storefront.</Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.active}
              onChange={() => handleToggle('active')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="windowDays">Reship Window (Days)</Label>
          <Description>How many days after delivery a customer can submit a reship request.</Description>
          <Input
            name="windowDays"
            type="number"
            value={form.windowDays}
            onChange={handleChange}
            disabled={!editing}
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="maxRequestsPerOrder">Max Requests Per Order</Label>
          <Description>Limits the number of reship requests a customer can make per order.</Description>
          <Input
            name="maxRequestsPerOrder"
            type="number"
            value={form.maxRequestsPerOrder}
            onChange={handleChange}
            disabled={!editing}
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="maxRequestsPerItem">Max Requests Per Item</Label>
          <Description>Restricts how many times a customer can request a reship for the same item.</Description>
          <Input
            name="maxRequestsPerItem"
            type="number"
            value={form.maxRequestsPerItem}
            onChange={handleChange}
            disabled={!editing}
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="maxActiveRequestsPerCustomer">Max Active Requests Per Customer</Label>
          <Description>Controls how many unresolved reship requests a customer can have at one time.</Description>
          <Input
            name="maxActiveRequestsPerCustomer"
            type="number"
            value={form.maxActiveRequestsPerCustomer}
            onChange={handleChange}
            disabled={!editing}
          />
        </FieldGroup>

        <FieldGroup>
          <Label>Require Reason</Label>
          <Description>Requires customers to enter a reason before submitting a reship request.</Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.requireReason}
              onChange={() => handleToggle('requireReason')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>

        <FieldGroup>
          <Label>Require Pictures</Label>
          <Description>Customers must upload photos of damaged or missing products with their request.</Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.requirePictures}
              onChange={() => handleToggle('requirePictures')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="shippingFeeCents">Shipping Fee (Cents)</Label>
          <Description>The shipping charge (in cents) that will be applied to approved reships.</Description>
          <Input
            name="shippingFeeCents"
            type="number"
            value={form.shippingFeeCents}
            onChange={handleChange}
            disabled={!editing}
          />
        </FieldGroup>

        <FieldGroup>
          <Label>Allow Retry After Rejection</Label>
          <Description>If enabled, customers may resubmit a reship request after it's been denied.</Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.allowRetryAfterRejection}
              onChange={() => handleToggle('allowRetryAfterRejection')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>

        <FieldGroup>
          <Label>Allow Multiple Items Per Reship</Label>
          <Description>Allows customers to include multiple items in a single reship request.</Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.allowMultipleItemsPerReship}
              onChange={() => handleToggle('allowMultipleItemsPerReship')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>

        <FieldGroup>
          <Label>Admin Approval Required</Label>
          <Description>Admins must manually approve reship requests before they are fulfilled.</Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.adminApprovalRequired}
              onChange={() => handleToggle('adminApprovalRequired')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>

        <FieldGroup>
          <Label>Notify Admin on Request</Label>
          <Description>Admins will receive a notification each time a new reship request is submitted.</Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.notifyAdminOnRequest}
              onChange={() => handleToggle('notifyAdminOnRequest')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>

        <FieldGroup>
          <Label>Notify Customer on Decision</Label>
          <Description>Customers will receive a notification once their reship request is approved or denied.</Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.notifyCustomerOnDecision}
              onChange={() => handleToggle('notifyCustomerOnDecision')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>
      </FieldSection>

      <ButtonRow>
        {editing ? (
          <>
            <Button onClick={() => setEditing(false)}>Cancel</Button>
            <Button primary onClick={handleSave}>Save</Button>
          </>
        ) : (
          <Button primary onClick={() => setEditing(true)}>Edit</Button>
        )}
      </ButtonRow>
    </Container>
  );
};

export default ReshipConfigurationDisplay;